(() => {
  const ELEMENT_NAME = 'product-gallery-badges';
  const OVERLAY_CLASS = 'arlaya-gallery-overlay-open';

  /*
   * Only elements that represent a genuinely open overlay are checked here.
   * We deliberately do not rely on body overflow classes: Shopify's editor and
   * some Shrine components can keep those classes temporarily after a refresh.
   */
  const OVERLAY_SELECTORS = [
    '#Details-menu-drawer-container[open]',
    '#Details-menu-drawer-container.menu-opening',
    'header-drawer details[open]',
    'details-modal details[open]',
    'cart-drawer.active',
    'cart-drawer[open]',
    '#CartDrawer.active',
    '#CartDrawer[open]',
    'cart-notification.active',
    'quick-add-modal[open]',
    'quick-add-modal.active',
    'product-modal[open]',
    'product-modal.active',
    'modal-dialog[open]',
    'modal-dialog.active',
    'pickup-availability-drawer[open]',
    'pickup-availability-drawer.active',
    '.popup-modal[open]',
    '.popup-modal.active',
    '.popup-modal--active',
    '.promo-popup.active',
    '.drawer.active',
    '.drawer.is-open',
    '.drawer[open]',
    '.sp-drawer.active',
    '.sp-drawer.is-open',
    '.sp-drawer[data-open="true"]',
    '[data-modal-open="true"]',
    '[data-drawer-open="true"]',
    '[role="dialog"][aria-modal="true"]:not([aria-hidden="true"])'
  ];

  const isVisible = (element) => {
    if (!element || !element.isConnected) return false;

    const style = window.getComputedStyle(element);
    if (
      style.display === 'none' ||
      style.visibility === 'hidden' ||
      Number.parseFloat(style.opacity || '1') === 0
    ) {
      return false;
    }

    const rect = element.getBoundingClientRect();
    return rect.width > 0 && rect.height > 0;
  };

  const isOverlayOpen = () => {
    for (const selector of OVERLAY_SELECTORS) {
      const matches = document.querySelectorAll(selector);
      for (const element of matches) {
        if (isVisible(element)) return true;
      }
    }
    return false;
  };

  let overlayFrame = null;
  const updateOverlayState = () => {
    if (overlayFrame !== null) return;

    overlayFrame = window.requestAnimationFrame(() => {
      overlayFrame = null;
      document.documentElement.classList.toggle(
        OVERLAY_CLASS,
        isOverlayOpen()
      );
    });
  };

  const startGlobalOverlayGuard = () => {
    if (window.__arlayaGalleryOverlayGuardStarted) return;
    window.__arlayaGalleryOverlayGuardStarted = true;

    const start = () => {
      updateOverlayState();

      const observer = new MutationObserver(updateOverlayState);
      observer.observe(document.documentElement, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: [
          'open',
          'class',
          'style',
          'aria-hidden',
          'aria-expanded',
          'data-open',
          'data-modal-open',
          'data-drawer-open'
        ]
      });

      document.addEventListener('click', updateOverlayState, true);
      document.addEventListener('keyup', updateOverlayState, true);
      document.addEventListener('transitionend', updateOverlayState, true);
      window.addEventListener('pageshow', updateOverlayState);
      window.addEventListener('resize', updateOverlayState);
    };

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', start, { once: true });
    } else {
      start();
    }
  };

  startGlobalOverlayGuard();

  if (customElements.get(ELEMENT_NAME)) return;

  class ProductGalleryBadges extends HTMLElement {
    constructor() {
      super();

      this.active = false;
      this.sectionId = '';
      this.template = null;
      this.selectedMediaPositions = new Set([1]);
      this.showAllMedia = false;
      this.retryTimers = [];
      this.mountTimer = null;
      this.cleanupTimer = null;
      this.domObserver = null;

      this.handleSectionRefresh = this.handleSectionRefresh.bind(this);
      this.handleDomMutation = this.handleDomMutation.bind(this);
    }

    connectedCallback() {
      /*
       * Shopify's editor can detach and reconnect the exact same section node
       * after saving. Cancel any delayed cleanup and initialise it again.
       */
      if (this.cleanupTimer) {
        window.clearTimeout(this.cleanupTimer);
        this.cleanupTimer = null;
      }

      this.sectionId = this.dataset.sectionId || '';
      this.template = this.querySelector('template');
      this.selectedMediaPositions = this.parseMediaSelection(
        this.dataset.selectedMedia || ''
      );
      this.showAllMedia = this.dataset.showAllMedia === 'true';

      if (!this.sectionId || !this.template) return;

      if (this.active) {
        this.scheduleMount(true, 0);
        return;
      }

      this.active = true;
      this.addLifecycleListeners();
      this.startDomObserver();
      this.mountBadges(true);

      [80, 250, 600, 1200, 2400].forEach((delay) => {
        this.retryTimers.push(
          window.setTimeout(() => {
            if (this.isConnected && this.active) this.mountBadges(false);
          }, delay)
        );
      });
    }

    disconnectedCallback() {
      this.active = false;
      this.stopRetryTimers();
      this.stopMountTimer();
      this.removeLifecycleListeners();
      this.stopDomObserver();

      /*
       * Delay removal because the editor often reconnects the same element in
       * the next rendering cycle. A reconnect cancels this timer above.
       */
      this.cleanupTimer = window.setTimeout(() => {
        this.cleanupTimer = null;
        if (this.isConnected) return;

        const replacementExists = Array.from(
          document.querySelectorAll(ELEMENT_NAME)
        ).some((element) => {
          return (
            element !== this &&
            element.dataset.sectionId === this.sectionId &&
            element.isConnected
          );
        });

        if (!replacementExists) this.removeBadges();
      }, 300);
    }

    addLifecycleListeners() {
      [
        'shopify:section:load',
        'shopify:section:select',
        'shopify:section:reorder',
        'shopify:block:select',
        'variant:change',
        'product:variant-change'
      ].forEach((eventName) => {
        document.addEventListener(eventName, this.handleSectionRefresh);
      });
    }

    removeLifecycleListeners() {
      [
        'shopify:section:load',
        'shopify:section:select',
        'shopify:section:reorder',
        'shopify:block:select',
        'variant:change',
        'product:variant-change'
      ].forEach((eventName) => {
        document.removeEventListener(eventName, this.handleSectionRefresh);
      });
    }

    startDomObserver() {
      this.stopDomObserver();
      this.domObserver = new MutationObserver(this.handleDomMutation);
      this.domObserver.observe(document.documentElement, {
        childList: true,
        subtree: true
      });
    }

    stopDomObserver() {
      if (!this.domObserver) return;
      this.domObserver.disconnect();
      this.domObserver = null;
    }

    stopRetryTimers() {
      this.retryTimers.forEach((timer) => window.clearTimeout(timer));
      this.retryTimers = [];
    }

    stopMountTimer() {
      if (!this.mountTimer) return;
      window.clearTimeout(this.mountTimer);
      this.mountTimer = null;
    }

    handleSectionRefresh(event) {
      if (!this.isConnected || !this.active) return;

      const refreshedSection = event && event.target;
      const isOwnSection =
        refreshedSection &&
        (refreshedSection === this || refreshedSection.contains(this));

      /* Re-read settings because the editor may have replaced the section node content. */
      this.template = this.querySelector('template') || this.template;
      this.selectedMediaPositions = this.parseMediaSelection(
        this.dataset.selectedMedia || ''
      );
      this.showAllMedia = this.dataset.showAllMedia === 'true';
      this.scheduleMount(Boolean(isOwnSection), isOwnSection ? 0 : 60);
      updateOverlayState();
    }

    handleDomMutation() {
      if (!this.isConnected || !this.active) return;
      this.scheduleMount(false, 80);
    }

    scheduleMount(force = false, delay = 60) {
      this.stopMountTimer();
      this.mountTimer = window.setTimeout(() => {
        this.mountTimer = null;
        if (this.isConnected && this.active) this.mountBadges(force);
      }, delay);
    }

    parseMediaSelection(value) {
      const positions = String(value || '')
        .split(',')
        .map((item) => Number.parseInt(item.trim(), 10))
        .filter((item) => Number.isInteger(item) && item >= 1 && item <= 30);

      return new Set(positions);
    }

    getBadgeSelector() {
      const safeId = String(this.sectionId)
        .replace(/\\/g, '\\\\')
        .replace(/"/g, '\\"');

      return `[data-arlaya-gallery-badges-owner="${safeId}"]`;
    }

    getProductRoots() {
      const roots = [];
      const productInfos = document.querySelectorAll(
        'product-info.main-product__info-container, product-info[id^="ProductInfo-"]'
      );

      productInfos.forEach((productInfo) => {
        const root =
          productInfo.closest('.shopify-section') ||
          productInfo.closest('.product') ||
          productInfo.parentElement;

        if (
          root &&
          root.querySelector(
            'media-gallery, .product__media-wrapper .product-media-slider'
          ) &&
          !roots.includes(root)
        ) {
          roots.push(root);
        }
      });

      roots.sort((a, b) => Number(isVisible(b)) - Number(isVisible(a)));
      return roots;
    }

    getHosts() {
      const selectors = [
        'media-gallery > slider-component.product-media-slider',
        'media-gallery slider-component.product-media-slider',
        'media-gallery .product-media-slider',
        '.product__media-wrapper slider-component.product-media-slider'
      ];

      const hosts = [];
      const roots = this.getProductRoots();

      roots.forEach((root) => {
        selectors.forEach((selector) => {
          root.querySelectorAll(selector).forEach((host) => {
            if (
              !host.closest(
                'quick-add-modal, product-modal, modal-dialog, .quick-add-modal'
              ) &&
              !hosts.includes(host)
            ) {
              hosts.push(host);
            }
          });
        });
      });

      if (!hosts.length) {
        selectors.forEach((selector) => {
          document.querySelectorAll(selector).forEach((host) => {
            if (
              !host.closest(
                'quick-add-modal, product-modal, modal-dialog, .quick-add-modal'
              ) &&
              isVisible(host) &&
              !hosts.includes(host)
            ) {
              hosts.push(host);
            }
          });
        });
      }

      return hosts;
    }

    getMediaTargets(host) {
      const mediaItems = Array.from(
        host.querySelectorAll('.product__media-item[data-media-id]')
      ).filter((item) => {
        return !item.classList.contains('hidden') && !item.hasAttribute('hidden');
      });

      /*
       * Avant l’initialisation complète de la galerie, on attend le prochain
       * passage plutôt que de poser les badges sur tout le carrousel.
       */
      if (!mediaItems.length) return [];

      if (this.showAllMedia) return mediaItems;

      return mediaItems.filter((item, index) => {
        return this.selectedMediaPositions.has(index + 1);
      });
    }

    mountBadges(force = false) {
      if (!this.isConnected || !this.active || !this.sectionId) return;

      this.template = this.querySelector('template') || this.template;
      if (!this.template) return;

      const galleries = this.getHosts();
      if (!galleries.length) return;

      const selector = this.getBadgeSelector();
      const targets = [];

      galleries.forEach((gallery) => {
        this.getMediaTargets(gallery).forEach((target) => {
          if (!targets.includes(target)) targets.push(target);
        });
      });

      document.querySelectorAll(selector).forEach((badge) => {
        if (!targets.includes(badge.parentElement)) {
          const oldHost = badge.parentElement;
          badge.remove();

          if (oldHost && !oldHost.querySelector('.arlaya-gallery-badges')) {
            oldHost.classList.remove('arlaya-gallery-badges-host');
          }
        }
      });

      targets.forEach((target) => {
        target.classList.add('arlaya-gallery-badges-host');

        const currentBadge = target.querySelector(`:scope > ${selector}`);
        if (currentBadge && !force) return;
        if (currentBadge) currentBadge.remove();

        const fragment = this.template.content.cloneNode(true);
        target.appendChild(fragment);
      });

      updateOverlayState();
    }

    removeBadges() {
      if (!this.sectionId) return;

      document.querySelectorAll(this.getBadgeSelector()).forEach((badge) => {
        const host = badge.parentElement;
        badge.remove();

        if (host && !host.querySelector('.arlaya-gallery-badges')) {
          host.classList.remove('arlaya-gallery-badges-host');
        }
      });
    }
  }

  customElements.define(ELEMENT_NAME, ProductGalleryBadges);
})();
